import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateTriageRequest,
  ICreateUpdateTriageItemRequest,
  ICreateUpdateTriageStepRequest,
  ITriageByVehicleResponse,
  ITriageItemsResponse,
  ITriageStepsResponse,
  ISingleTriageStepResponse,
  ISingleTriageResponse,
} from "../models/Triage";

export default function useTriage(triageId?: string, vehicleId?: string, includeAllSteps = false) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const triage = useQuery({
    queryKey: ["triage", triageId],
    queryFn: () => get<ISingleTriageResponse>(`/triage/${triageId}`),
    enabled: !!triageId,
    staleTime: 1000 * 60 * 2,
  });

  const triageByVehicle = useQuery({
    queryKey: ["triage-vehicle", vehicleId],
    queryFn: () => get<ITriageByVehicleResponse>(`/triage/vehicle/${vehicleId}`),
    enabled: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const triageItems = useQuery({
    queryKey: ["triage-items", triageId],
    queryFn: () => get<ITriageItemsResponse>(`/triage/${triageId}/items`),
    enabled: !!triageId,
    staleTime: 1000 * 60,
  });

  const triageSteps = useQuery({
    queryKey: ["triage-steps"],
    queryFn: () => get<ITriageStepsResponse>(`/triage/steps?activeOnly=true`),
    staleTime: 1000 * 60 * 10,
  });

  const allTriageSteps = useQuery({
    queryKey: ["triage-steps-all"],
    queryFn: () => get<ITriageStepsResponse>(`/triage/steps`),
    enabled: includeAllSteps,
    staleTime: 1000 * 60 * 10,
  });

  const createTriage = useMutation(
    (body: ICreateTriageRequest) => post<ICreateTriageRequest, ISingleTriageResponse>("/triage", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["triage", triageId]);
        queryClient.invalidateQueries(["triage-vehicle", vehicleId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["pagedTable", "/triage/filtered"]);
      },
    }
  );

  const saveTriageItem = useMutation(
    (body: ICreateUpdateTriageItemRequest) => post("/triage/items", body),
    {
      onSuccess: (_, body) => {
        // A system step may have just written to the vehicle or its lead.
        queryClient.invalidateQueries(["system-step-values", body.vehicleId]);
        queryClient.invalidateQueries(["vehicles", body.vehicleId]);
        queryClient.invalidateQueries(["triage", triageId]);
        queryClient.invalidateQueries(["triage-items", triageId]);
        queryClient.invalidateQueries(["triage-vehicle"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["pagedTable", "/triage/filtered"]);
        // Completing the last step closes the triage session server-side, so
        // refresh the timer card to reflect the stopped clock immediately.
        queryClient.invalidateQueries(["triageSessions", triageId]);
      },
    }
  );

  const createTriageStep = useMutation(
    (body: ICreateUpdateTriageStepRequest) => post<ICreateUpdateTriageStepRequest, ISingleTriageStepResponse>("/triage/steps", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["triage-steps"]);
        queryClient.invalidateQueries(["triage-steps-all"]);
      },
    }
  );

  const updateTriageStep = useMutation(
    (body: ICreateUpdateTriageStepRequest) => put<ICreateUpdateTriageStepRequest, ISingleTriageStepResponse>(`/triage/steps/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["triage-steps"]);
        queryClient.invalidateQueries(["triage-steps-all"]);
      },
    }
  );

  const updateTriageStepOrder = useMutation(
    ({ triageStepId, newIndex }: { triageStepId: string, newIndex: number }) => put(`/triage/steps/${triageStepId}/order?newIndex=${newIndex}`, undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["triage-steps"]);
        queryClient.invalidateQueries(["triage-steps-all"]);
      },
    }
  );

  const deleteTriageStep = useMutation(
    (triageStepId: string) => del(`/triage/steps/${triageStepId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["triage-steps"]);
        queryClient.invalidateQueries(["triage-steps-all"]);
      },
    }
  );

  return {
    triage,
    triageByVehicle,
    triageItems,
    triageSteps,
    allTriageSteps,
    createTriage,
    saveTriageItem,
    createTriageStep,
    updateTriageStep,
    updateTriageStepOrder,
    deleteTriageStep,
  };
}
